import { motion } from "framer-motion";
import { Heart, MessageCircle, Send } from "lucide-react";
import { Overline, Reveal, ease } from "./Primitives";
import { useT } from "@/lib/i18n";

const IMG_A = "https://images.unsplash.com/photo-1504198322253-cfa87a0ff25f?auto=format&fit=crop&w=1400&q=80";

const Cell = ({ className = "", children, testid, delay = 0 }) => (
  <Reveal delay={delay} className={className}>
    <div data-testid={testid} className="bento-cell h-full rounded-[28px] border border-[var(--line)] bg-[var(--card)] overflow-hidden relative">
      {children}
    </div>
  </Reveal>
);

const Text = ({ t, d }) => (
  <div className="relative z-10">
    <h3 className="font-display text-3xl md:text-4xl font-light">{t}</h3>
    <p className="mt-3 text-stone-300 text-sm md:text-base leading-relaxed max-w-sm">{d}</p>
  </div>
);

const Phone = () => (
  <div className="w-[150px] h-[290px] rounded-[30px] border-[6px] border-stone-800 bg-[#0c0a09] p-2 shadow-2xl">
    <div className="h-full rounded-[22px] bg-[var(--bg2)] p-3 flex flex-col gap-2">
      <div className="flex items-center gap-2"><div className="w-6 h-6 rounded-full bg-[var(--gold)]" /><div className="h-2 w-16 rounded bg-stone-700" /></div>
      <div className="flex-1 rounded-xl overflow-hidden"><img src="/lene.jpg" alt="" className="w-full h-full object-cover object-top" /></div>
      <div className="flex gap-3 text-stone-300"><Heart size={14} className="text-rose-400 fill-rose-400" /><MessageCircle size={14} /><Send size={14} /></div>
      <div className="h-2 w-3/4 rounded bg-stone-700" /><div className="h-2 w-1/2 rounded bg-stone-800" />
    </div>
  </div>
);

const Spark = () => (
  <svg viewBox="0 0 400 120" className="w-full h-28 md:h-36" preserveAspectRatio="none">
    <defs>
      <linearGradient id="sg" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#e6c594" stopOpacity="0.35" /><stop offset="1" stopColor="#e6c594" stopOpacity="0" /></linearGradient>
    </defs>
    <motion.path d="M0 110 C40 100 60 95 90 85 S150 90 180 70 S240 60 270 45 S340 30 400 8 L400 120 L0 120Z" fill="url(#sg)"
      initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1.2, delay: 0.6 }} />
    <motion.path d="M0 110 C40 100 60 95 90 85 S150 90 180 70 S240 60 270 45 S340 30 400 8" fill="none" stroke="#e6c594" strokeWidth="2"
      initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 2, ease }} />
  </svg>
);

export const Bento = () => {
  const t = useT().bento;
  return (
    <section id="work" className="py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <Overline>{t.over}</Overline>
          <h2 className="mt-6 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl">{t.title}</h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-5 md:auto-rows-[340px]">
          <Cell className="md:col-span-8" testid="bento-storytelling">
            <img src={IMG_A} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 transition-transform duration-[1.5s] group-hover:scale-105 bento-img" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0c0a09] via-[#0c0a09]/50 to-transparent" />
            <div className="relative h-full min-h-[320px] p-8 md:p-10 flex flex-col justify-end"><Text {...t.a} /></div>
          </Cell>
          <Cell className="md:col-span-4" testid="bento-convert" delay={0.1}>
            <div className="h-full min-h-[300px] p-8 md:p-10 flex flex-col justify-between">
              <Text {...t.b} />
              <div className="rounded-2xl border border-[var(--line)] p-4 bg-[#0c0a09]/60">
                <div className="h-2 w-2/3 rounded bg-stone-700" /><div className="mt-2 h-2 w-1/2 rounded bg-stone-800" />
                <div className="mt-4 inline-flex rounded-full bg-[var(--gold)] text-[#0c0a09] text-xs font-semibold px-4 py-2 pulse-gold">{t.b.cta}</div>
              </div>
            </div>
          </Cell>
          <Cell className="md:col-span-4" testid="bento-mobile" delay={0.1}>
            <div className="h-full min-h-[420px] md:min-h-0 p-8 md:p-10 flex flex-col gap-6">
              <Text {...t.c} />
              <div className="flex-1 flex justify-center items-start md:-mb-24"><Phone /></div>
            </div>
          </Cell>
          <Cell className="md:col-span-8" testid="bento-growth" delay={0.2}>
            <div className="h-full min-h-[320px] p-8 md:p-10 flex flex-col justify-between gap-6">
              <Text {...t.d} />
              <Spark />
            </div>
          </Cell>
        </div>
      </div>
    </section>
  );
};
